import { ReactNode } from 'react';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { UserPlan } from '../types';
import { IS_DEV } from '../config';
import { storage } from '../lib/utils/storage';

export type EditorStep = 'initial' | 'screen-win' | 'screen-loose' | 'screen-disabled';

export type WixUser = {
  plan: UserPlan;
  accessToken: string;
  embedScriptInjected: boolean;
  shouldSetTz: boolean;
};

const strToBool = (value?: string | null): boolean => {
  if (value && value === 'true') {
    return true;
  }

  return false;
};

const getUserData = (): AppState['wixUser'] => {
  const searchParams = new URLSearchParams(window.location.search);

  const accessToken = searchParams.get('token') || '';
  const vendorProductId = searchParams.get('vendorProductId') as UserPlan;
  const embedScriptInjected = strToBool(searchParams.get('embedScriptInjected'));
  const tz = searchParams.get('timeZone');

  if (IS_DEV) {
    return {
      plan: vendorProductId || storage.getItem('userPlan'),
      accessToken: accessToken || storage.getItem('wixDevAccessToken'),
      embedScriptInjected: searchParams.get('embedScriptInjected')
        ? embedScriptInjected
        : strToBool(storage.getItem('embedScriptInjected')),
      shouldSetTz: false,
    };
  }

  return {
    plan: vendorProductId,
    accessToken,
    embedScriptInjected,
    shouldSetTz: !tz,
  };
};

interface AppState {
  sidebarVisible: boolean;
  sidebarContent: ReactNode;
  editor: {
    step: EditorStep;
  };
  wixUser?: WixUser;
  tarifInfoPopupVisible: boolean;
}

const initialState: AppState = {
  sidebarVisible: false,
  sidebarContent: null,
  editor: {
    step: 'initial',
  },
  wixUser: getUserData(),
  tarifInfoPopupVisible: false,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    openSidebar: (state, action: PayloadAction<{ content: ReactNode }>) => {
      state.sidebarVisible = true;
      state.sidebarContent = action.payload.content;
    },
    closeSidebar: (state) => {
      state.sidebarVisible = false;
    },
    setSidebarContent: (state, action: PayloadAction<{ content: ReactNode }>) => {
      state.sidebarContent = action.payload.content;
    },
    setEditorStep: (state, action: PayloadAction<{ step: EditorStep }>) => {
      state.editor.step = action.payload.step;
    },
    openTarifInfoPopup: (state) => {
      state.tarifInfoPopupVisible = true;
    },
    closeTarifInfoPopup: (state) => {
      state.tarifInfoPopupVisible = false;
    },
  },
});

export const { openSidebar, closeSidebar, setSidebarContent, setEditorStep, openTarifInfoPopup, closeTarifInfoPopup } =
  appSlice.actions;
